/**
 * Pure helpers for calendar hour-input drafts: free-form string while typing,
 * numeric parse for sums/payload, and per-row validation (client UX only —
 * server/DB still enforce integrity).
 */

import { round1 } from "./aggregation";

/** Format a stored/derived hour value for the input binding. */
export function formatHoursInput(n: number): string {
  if (!Number.isFinite(n)) return "0";
  return String(round1(n));
}

/**
 * Parse a draft string into a non-negative finite number.
 * Accepts a trailing "." while typing (`"7."` → 7). Empty / garbage → null.
 */
export function parseHoursInput(raw: string): number | null {
  const s = raw.trim();
  if (s === "") return null;
  // Intermediate "7." while the user is still typing the decimal part.
  if (/^\d+\.$/.test(s)) {
    return Number(s.slice(0, -1));
  }
  if (!/^\d+(\.\d+)?$/.test(s)) return null;
  const n = Number(s);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

/** Normalize a draft on blur / before POST (round1). Null when unparseable. */
export function normalizeHoursInput(raw: string): number | null {
  const parsed = parseHoursInput(raw);
  if (parsed === null) return null;
  return round1(parsed);
}

export type HoursFieldIssue = "unparseable" | "overCap" | "rdGtTotal";

export interface RowHoursValidation {
  totalInvalid: boolean;
  rdInvalid: boolean;
  totalIssues: HoursFieldIssue[];
  rdIssues: HoursFieldIssue[];
}

export interface ValidateRowInput {
  totalHoursInput: string;
  rdHoursInput: string;
  /** When false, blank ACTUAL is allowed (existing weekActualRequired gate handles save). */
  rdEntered: boolean;
  hoursCap: number;
}

/**
 * Flag a day row when Gesamt drafts are unparseable / over the daily cap, or when
 * R&D exceeds Gesamt. ACTUAL is capped by Gesamt only (not dailyCap) — C2.
 * Pure — safe for unit tests without React.
 */
export function validateRow(input: ValidateRowInput): RowHoursValidation {
  const totalIssues: HoursFieldIssue[] = [];
  const rdIssues: HoursFieldIssue[] = [];

  const totalParsed = parseHoursInput(input.totalHoursInput);
  if (totalParsed === null) {
    totalIssues.push("unparseable");
  } else if (totalParsed > input.hoursCap + 1e-9) {
    totalIssues.push("overCap");
  }

  const rdRaw = input.rdHoursInput.trim();
  const rdRelevant = input.rdEntered || rdRaw !== "";
  let rdParsed: number | null = null;
  if (rdRelevant) {
    rdParsed = parseHoursInput(input.rdHoursInput);
    if (rdParsed === null) {
      rdIssues.push("unparseable");
    }
  }

  if (totalParsed !== null && rdParsed !== null && rdParsed > totalParsed + 1e-9) {
    rdIssues.push("rdGtTotal");
  }

  return {
    totalInvalid: totalIssues.length > 0,
    rdInvalid: rdIssues.length > 0,
    totalIssues,
    rdIssues,
  };
}

export function rowHasHoursError(v: RowHoursValidation): boolean {
  return v.totalInvalid || v.rdInvalid;
}

/** Which hour input currently has focus (inline UX only — save still uses raw validateRow). */
export type FocusedHoursField = "total" | "rd" | null;

/**
 * While a field is focused, hide transient `unparseable` issues (`""`, `"abc"`, `"."`)
 * so backspace/retype is not interrupted. Keep `overCap` / `rdGtTotal` visible immediately.
 */
export function suppressFocusedUnparseable(
  v: RowHoursValidation,
  focused: FocusedHoursField,
): RowHoursValidation {
  if (focused === null) return v;
  const totalIssues =
    focused === "total" ? v.totalIssues.filter((issue) => issue !== "unparseable") : v.totalIssues;
  const rdIssues =
    focused === "rd" ? v.rdIssues.filter((issue) => issue !== "unparseable") : v.rdIssues;
  return {
    totalIssues,
    rdIssues,
    totalInvalid: totalIssues.length > 0,
    rdInvalid: rdIssues.length > 0,
  };
}
